/**
 * Where a gardener stands with paying, and the doors to Stripe; pausing a
 * monthly plan and taking the pause away; the reminder email's one-tap
 * cancel; claiming a founding-gardener link with an account that already
 * exists; and what the signed-out landing page says about price, the trial,
 * Free and the founding welcome.
 *
 * The server decides all of it (architecture audit batch 8): the status
 * line's kind (Access.statusLine), whether "Cancel subscription" and a
 * pause may be offered (Access.canCancel, Access.canPause), the plan comparison's rows (Plans.rows) and the landing
 * page's facts (getLandingFacts). The app maps a kind to its words and
 * formats the dates; it works none of them out.
 *
 * Nothing about a card comes near this code: checkout and the portal are
 * Stripe's own pages, and all this does is ask the backend for their URLs.
 *
 * Prices are Stripe's, read through the backend, as this gardener would be
 * charged: before the launch ends (and for a founding gardener) the yearly
 * and monthly prices are first-year prices, and the page says "for your
 * first year"; no price is promised beyond it. The AI allowance ("Photo
 * help") is not here: api/aiAllowance.
 *
 * Endpoints (backend routes/billing; every answer also carries `success`):
 * - GET  /billing/status → BillingStatus.
 * - POST /billing/checkout { interval } → { url } (Stripe Checkout); like the
 *   portal, pause and resume, refused 403 TENDING_OWNER_PLAN in someone else's garden.
 * - POST /billing/portal [{ flow: 'cancel' }] → { url } (Stripe's portal).
 * - GET  /billing/pause → PauseOffer.
 * - POST /billing/pause { resumeAt } → { access }.
 * - POST /billing/resume → { chargedNow, access }.
 * - GET  /billing/stop?t=<token> and POST /billing/stop { token } → StopLink
 *   (the reminder email's one-tap cancel; no sign-in, the token is trusted).
 * - POST /billing/sync { sessionId } → { access } (after Checkout returns).
 * - POST /billing/founding { code } → FoundingClaimResult.
 * - GET  /billing/landing?founding=&from= → LandingFacts (no sign-in).
 *
 * Who uses it: features/billing (useBilling, SubscribePage,
 * SubscribeSuccess, StopSubscriptionPage, PauseOffer, FoundingClaim,
 * SubscriptionWall, PlanComparison), features/landing (LandingPage and its
 * sections, getLandingFacts and LandingFacts) and api/gifts (its types).
 *
 * Caching: features/billing/lib/useBilling keeps the status under
 * BILLING_QUERY_KEY (['billing'], BILLING_STALE_MS); the pause offer under
 * [...BILLING_QUERY_KEY, 'pause']. Anything that changes access (checkout's
 * return, pausing, a founding claim, a gift) invalidates ['billing'].
 *
 * Auth: the signed-in person's token. Billing is the person's, but a member
 * tending someone else's garden (X-Grow-Garden) uses that owner's plan (B23):
 * GET /billing/status then answers the owner's plan with tendingFor (whose,
 * and the line to show), and checkout, the portal, pausing and resuming are
 * refused 403 TENDING_OWNER_PLAN there. Free's plant counts are always the
 * garden being tended's (Access.limits).
 *
 * @module billing
 */

import { apiClient } from './apiClient';
import { trackEvent } from '@/shared/lib/analytics';
import { getLanguage } from '@/shared/i18n/i18nSetup';

/**
 * Paying, a monthly plan paused (readable, nothing changed), in the free
 * trial, or on Free (a trial or subscription that ended: a few plants
 * growing, never read-only).
 */
export type AccessState = 'active' | 'paused' | 'trial' | 'free';
/** The two plans: monthly or yearly. */
export type BillingInterval = 'month' | 'year';

/** A founding gardener: free until the founding day, then the founder price for their first year. */
export interface Founding {
  /** When they became one, as the server sends it; absent in someone else's garden (B23: a member sees only freeUntil). */
  since?: string;
  /** The last day of their free period, YYYY-MM-DD (a day, not a moment); null when unknown. */
  freeUntil: string | null;
  /**
   * Their founder price as words ("$39 a year"), for their first year (no
   * price is promised beyond it; decided 2026-10-07); null when not recorded;
   * absent in someone else's garden (B23).
   */
  price?: string | null;
}

/** Free's plants in the garden being tended: how many grow, and the room left (up to Free's plants). */
export interface FreePlantLimits {
  /** On Free: adding is limited to the room. False otherwise (the counts are then null). */
  limited: boolean;
  /** Up to how many plants Free has. */
  limit: number;
  /** Plants growing now (the example plant not counted); a garden may have more than the limit, all kept. */
  growing: number | null;
  /** How many more fit; none while the limit or more are growing. */
  room: number | null;
}

/** Which sentence the subscribe page says about where a gardener stands: the app's words billing.status.<kind>. */
export type StatusLineKind =
  | 'paused' | 'pausedOpen' | 'ending' | 'paymentProblem' | 'pauseAhead' | 'active'
  | 'founding' | 'gift' | 'trialWaiting' | 'trialOne' | 'trial' | 'free';

/** The subscribe page's sentence about where a gardener stands, as the server decides it. */
export interface StatusLine {
  /** Which sentence. */
  kind: StatusLineKind;
  /** ending and active: the period's end (ISO); founding: the last free day, and gift: its last day (YYYY-MM-DD). */
  date?: string | null;
  /** pauseAhead: the day the pause starts (ISO). */
  from?: string | null;
  /** paused and pauseAhead: the day billing starts again (ISO); a pause with no end is kind pausedOpen instead. */
  until?: string | null;
  /** active: the plan paid on. */
  plan?: BillingInterval | null;
  /** gift: who gave it. */
  giftFrom?: string;
  /** trialWaiting and trial: the trial's days (left). */
  days?: number;
  /** free: up to how many plants Free has. */
  plants?: number;
}

/** What a gardener may do, and where they stand with paying. */
export interface Access {
  /** Paying, paused, in the trial, or on Free. */
  state: AccessState;
  /** False only while a monthly plan is paused (nothing changed until it resumes); Free can write, within its limits. */
  canWrite: boolean;
  /**
   * Whether the model is theirs to use.
   *
   * Answered by the server with the same rule that guards the endpoints,
   * allowlist included, so a screen cannot offer what the gate would refuse.
   */
  ai: boolean;
  /**
   * True when the owner has switched AI off for this account (or for the
   * garden's owner) on the Oversight page: ai is then false, and the app says
   * so (billing.aiRevoked) rather than offering Subscribe.
   */
  aiRevoked: boolean;
  /** When the trial ends, as the server sends it; null when not in one, or while it waits for the first plant. */
  trialEndsAt: string | null;
  /** Whole days of trial left (its full length while it waits for the first plant); null when not in one. */
  daysLeft: number | null;
  /** False while a trial from the first plant waits for that plant; null when not in a trial. */
  trialStarted: boolean | null;
  /** How the trial is counted: set by hand (a founder, or extended by the owner), or 14 days from the first plant. */
  trialKind: 'set' | 'firstPlant' | null;
  /** When Free began, as the server sends it; null when not on Free. */
  freeSince: string | null;
  /** Free's limits in the garden being tended; sent by GET /billing/status only (the pause, resume, sync, founding and gift answers carry none). */
  limits?: { plants: FreePlantLimits };
  /** When the paid period ends or renews. */
  currentPeriodEnd: string | null;
  /** True when they have cancelled and it runs out at currentPeriodEnd. */
  cancelAtPeriodEnd: boolean;
  /** True when Stripe could not take the last payment. */
  paymentProblem: boolean;
  /** The plan they pay on, or null. */
  interval: BillingInterval | null;
  /** True once Stripe knows them, so the portal has somebody to show. */
  hasCustomer: boolean;
  /** A paused plan's pause starts then (the end of the month paid for), as the server sends it; null when not paused. */
  pausedFrom?: string | null;
  /** A paused plan's billing starts again then; null when not paused. */
  pausedUntil?: string | null;
  /** A founding gardener (the header says so in place of the days left); null for anyone else. */
  founding: Founding | null;
  /**
   * The trial is a gift of a year of Grow More (features/gift): who gave it (never sent in someone else's garden, B23), and its last day (YYYY-MM-DD).
   * The header says "Gift" in place of the days left. Null otherwise.
   */
  gift: { from?: string; endsOn: string | null } | null;
  /** Whether the subscribe page offers "Cancel subscription": a running monthly plan (trialing too), not set to end, with no pause. */
  canCancel: boolean;
  /** Whether that link offers a pause first: an active monthly plan, not set to end, with no pause ahead or under way. */
  canPause: boolean;
  /** Whether the subscribe page offers a plan to pay for (Choose, the after-trial line); false for a founder while their year is free. */
  canChoosePlan: boolean;
  /** What the subscribe page says about where they stand. */
  statusLine: StatusLine;
  /** C118: a running paid plan (the server's state, as a flag, so the app compares no state). */
  active: boolean;
  /** C118: the plan is paused. */
  paused: boolean;
  /** C118: a pause is set for later on a running plan. */
  pauseAhead: boolean;
  /** C118: a paid plan, running or paused: the subscribe page shows the buttons to manage it. */
  hasPlan: boolean;
  /** C118: on the Free plan. */
  onFree: boolean;
}

/** A cell of the plan comparison: included, not included, or the key of its words (billing.compare.values.<key>). */
export type PlanCell = boolean | string;

/** One row of the plan comparison, as the server decides it from the rule that enforces it. */
export interface PlanRow {
  /** Its words' key, billing.compare.rows.<key>. */
  key: string;
  /** What Free has. */
  free: PlanCell;
  /** What Grow More has. */
  grow: PlanCell;
}

/** A plan's price. */
export interface Price {
  /** In the smallest unit: 499 is $4.99. */
  amount: number;
  /** Stripe's lowercase currency code, e.g. usd. */
  currency: string;
}

/** What the subscribe page says about the plans, besides their prices. */
export interface Plans {
  /**
   * The yearly price this gardener would be charged (a first-year price: the
   * launch's, or a founder's), with a twelfth of it a month ("$3.25 a month").
   */
  year: Price & { perMonth: number };
  /** The monthly price this gardener would be charged (during the launch, a first-year price too). */
  month: Price;
  /**
   * While the launch lasts, and only from the day the page may say so (the Grow setting
   * subscription.SHOW_LAUNCH_LINE_FROM, January 2027): the launch's last day (until, YYYY-MM-DD) and the
   * server's finished sentence ("Join before April 1 and lock in $39 for your first year.", in the gardener's
   * language; L6). The prices after the launch are not sent. Null before then, after it, and for a founding
   * gardener. Before it, year and month are first-year prices.
   */
  launch: { until: string; words: string | null } | null;
  /** A founding gardener: the yearly price is their founder price, for their first year. */
  founderPrice: boolean;
  /**
   * A founding gardener's first-year price said whole, in their language ("$39 for your first year"; the server's word
   * founding.firstYear, C101); null for anyone else, or when the server could not say it. Always sent (C118 F3).
   */
  founderPriceWords: string | null;
  /** How many monthly payments a pause may skip. */
  pauseMaxMonths: number;
  /** What the comparison's Free column counts: up to how many plants. */
  free: { plants: number };
  /** The comparison's rows, Free's cells beside Grow More's. */
  rows: PlanRow[];
  /**
   * What the plan card and lines draw, as the server decides it (B69): the founder's line, and whether
   * the monthly link, the saving line (and the saving, savingAmount, in the prices' unit, C118 F5) and the Best value pill show. Always sent (B69, C118 F3).
   */
  display: { line: 'founder' | 'standard'; monthly: boolean; saving: boolean; savingAmount: number; bestValue: boolean };
}

/** The access GET /billing/status answers: Free's limits are always there (the other answers carry none). */
export type StatusAccess = Access & {
  limits: { plants: FreePlantLimits };
  /** C118 F6: the line "your trial starts with your first plant" shows (a trial waiting for the first plant, not a founder's). The status answer only. */
  showTrialLine: boolean;
};

/** Whose plan a member tending someone else's garden is on, as the server says it (B23). */
export interface TendingFor {
  /** The garden owner's name. */
  name: string;
  /** The line the subscribe page shows, in the reader's language ("You're tending Friday's garden on their plan."). */
  line: string;
}

/** What the subscribe page needs: the gardener's access, both prices and the trial's length (from the first plant). */
export interface BillingStatus {
  /** Where the gardener stands. */
  access: StatusAccess;
  /** Each plan's price, as this gardener would be charged. */
  prices: Record<BillingInterval, Price>;
  /** What the page says about the plans. */
  plans: Plans;
  /** How long the free trial is, in days. */
  trialDays: number;
  /** False on a server with no Stripe key; the subscribe page says so. */
  configured: boolean;
  /**
   * In someone else's garden: whose plan it is, and the line to show; everything above is then that owner's plan
   * (nothing on it can be changed from here). Null in the gardener's own garden.
   */
  tendingFor: TendingFor | null;
}

/**
 * Where the gardener stands with paying, and what the plans cost.
 *
 * @returns Their access, the prices and the trial's length.
 */
export async function getBillingStatus(): Promise<BillingStatus> {
  const response = await apiClient.get<BillingStatus & { success: boolean }>('/billing/status', { params: { lang: getLanguage() } });
  return response.data;
}

/**
 * Stripe Checkout for one of the two plans. Resolves with the page to go to.
 *
 * @param interval - The plan.
 * @returns Stripe's checkout address.
 */
export async function startCheckout(interval: BillingInterval): Promise<string> {
  const response = await apiClient.post<{ success: boolean; url: string }>('/billing/checkout', { interval });
  trackEvent('subscribe_started', { interval });
  return response.data.url;
}

/**
 * Stripe's customer portal: change card, switch plan, cancel.
 *
 * @param flow - 'cancel' opens it straight on cancelling (after the pause offer was turned down).
 * @returns The portal's address.
 */
export async function openPortal(flow?: 'cancel'): Promise<string> {
  const response = await apiClient.post<{ success: boolean; url: string }>('/billing/portal', flow ? { flow } : undefined);
  return response.data.url;
}

/** Why a plan cannot be paused, as the server says it. */
export type PauseUnavailable = 'NO_SUBSCRIPTION' | 'NOT_ACTIVE' | 'CANCELLING' | 'NOT_MONTHLY' | 'ALREADY_PAUSED' | 'NO_DAYS';

/** Whether the gardener's plan can be paused, and until when. */
export interface PauseOffer {
  /** True for an active monthly plan, not cancelling or paused already. */
  available: boolean;
  /** Why not, when not. */
  reason: PauseUnavailable | null;
  /** The renewal days billing may start again on (ISO moments), earliest first. */
  options: string[];
  /** The one to offer first: the renewal nearest the garden's own last spring frost (March 1 where it has no frost dates). */
  suggested: string | null;
  /** When the pause would start: the end of the month paid for. */
  pausedFrom: string | null;
  /**
   * A monthly plan's month as charged now, a first year's discount taken off:
   * charged again from the day chosen, or, for a plan already paused, what
   * resuming charges today. Null for any other plan.
   */
  price: Price | null;
}

/**
 * Whether the gardener may pause their monthly plan, and the days they may choose.
 *
 * @returns The offer.
 */
export async function getPauseOffer(): Promise<PauseOffer> {
  const response = await apiClient.get<PauseOffer & { success: boolean }>('/billing/pause');
  return response.data;
}

/**
 * Pauses the monthly plan until one of the offered days.
 *
 * @param resumeAt - One of the offer's options.
 * @returns Their access now.
 */
export async function pausePlan(resumeAt: string): Promise<Access> {
  const response = await apiClient.post<{ success: boolean; access: Access }>('/billing/pause', { resumeAt });
  return response.data.access;
}

/**
 * Takes the pause away: nothing charged before it has begun; once it has, a new month starts and is charged today.
 *
 * @returns Whether a month was charged now, and their access.
 */
export async function resumePlan(): Promise<{ chargedNow: boolean; access: Access }> {
  const response = await apiClient.post<{ success: boolean; chargedNow: boolean; access: Access }>('/billing/resume');
  return { chargedNow: response.data.chargedNow, access: response.data.access };
}

/** What a reminder email's one-tap cancel link would stop, or did. */
export interface StopLink {
  /** unknown: no such link; expired: too old; nothing: no subscription to stop; stopped: set to end; ready: it can be stopped. */
  status: 'unknown' | 'expired' | 'nothing' | 'stopped' | 'ready';
  /** When it ends (once stopped) or its period ends, as the server sends it. */
  endsOn: string | null;
  /** When billing would start again after the pause; null when not paused, or for a pause with no end. */
  pausedUntil: string | null;
}

/**
 * Asks what a one-tap cancel link would stop. Needs no sign-in.
 *
 * @param token - From the link.
 * @returns Its state.
 */
export async function readStopLink(token: string): Promise<StopLink> {
  const response = await apiClient.get<StopLink & { success: boolean }>('/billing/stop', { params: { t: token } });
  return response.data;
}

/**
 * The one tap: the subscription ends at the end of its period, so nothing more is charged. Needs no sign-in.
 *
 * @param token - From the link.
 * @returns Its state after.
 */
export async function confirmStopLink(token: string): Promise<StopLink> {
  const response = await apiClient.post<StopLink & { success: boolean }>('/billing/stop', { token });
  return response.data;
}

/**
 * After Checkout sends the gardener back: make sure we have heard, rather
 * than wait for Stripe's webhook.
 *
 * @param sessionId - The Checkout session Stripe sent them back with.
 * @returns Their access now.
 */
export async function syncCheckout(sessionId: string): Promise<Access> {
  const response = await apiClient.post<{ success: boolean; access: Access }>('/billing/sync', { sessionId });
  return response.data.access;
}

/** What claiming a founding link answers. */
export interface FoundingClaimResult {
  /** Made a founding gardener now, or one already; false for a wrong or closed code (said nothing about). */
  founding: boolean;
  /** Already one before this. */
  already: boolean;
  /** Every founding place was taken, so nobody was made one (L7); the claim says so kindly. */
  full?: boolean;
  /** The yearly price a founding gardener pays for their first year; null or absent for a wrong or closed code (the answer then says only that it is not founding). */
  price?: Price | null;
  /** That price said whole in the app's language ("$39 for your first year"; the server's word founding.firstYear, C101); null or absent when not founding. */
  priceWords?: string | null;
  /** The line that says when the founder's first year starts and that there is nothing to do before it, in the gardener's language (L6). */
  startsWords?: string | null;
  /** Their access now; absent for a wrong or closed code. */
  access?: Access;
}

/**
 * Claims a founding link (?founding=CODE) for an account that already
 * exists: while the code is open it makes them a founding gardener, as
 * signing up through it would have.
 *
 * @param code - The founding code the link brought.
 * @returns Whether they are a founding gardener now, and their access.
 */
export async function claimFounding(code: string): Promise<FoundingClaimResult> {
  const response = await apiClient.post<FoundingClaimResult & { success: boolean }>('/billing/founding', { code }, { params: { lang: getLanguage() } });
  if (response.data.founding && !response.data.already) trackEvent('founding_claimed');
  return response.data;
}

/** Which prices are in force: the launch's, the launch's with its end said, or those after it. */
export type PricePhase = 'launch' | 'launchLine' | 'after';

/** What the landing page says about price, the trial, Free and the founding welcome, as the server decides it. */
export interface LandingFacts {
  /** Which prices are in force, by the server's clock. */
  phase: PricePhase;
  /** The yearly and monthly prices a new subscriber is charged now (the prices after the launch are not sent). */
  prices: { year: Price; month: Price };
  /** The first day of the later prices, YYYY-MM-DD. */
  launchEnds: string;
  /**
   * The launch sentence, finished, in English ("Join before April 1 and lock in $39 for your first year, or $7.99 a
   * month."; the server's words launch.joinBeforeMonthly, L6). Null once the later prices are in force, for a founding
   * visitor, and when the server could not say it.
   */
  launchWords: string | null;
  /** The founding welcome, for a visitor whose link carried the open founding code; null otherwise. */
  founding: {
    /** The last free day, YYYY-MM-DD. */
    freeUntil: string;
    /**
     * The first year's price said whole, in the page's language ("$39 for your first year", "$39 el primer año"): the
     * server's word founding.firstYear with the real founder price (C101). Null when the server could not say it.
     */
    priceWords: string | null;
    /**
     * When the first year starts and that there is nothing to do before ("Your $39 first year starts June 30, 2027.
     * There's nothing to do before then."), in English; the server's word founding.startsLine (L6). Null when unsaid.
     */
    startsWords: string | null;
    /** The founder's first-year yearly price. */
    price: Price;
  } | null;
  /** The open founding code came, but every founding place is taken (L7): the hero shows the full state and the waiting list. */
  foundingFull: boolean;
  /** The trial's days from the first plant: the follower trial's for a visitor from the owner's own links. */
  trialDays: number;
  /** Up to how many plants Free has. */
  freePlants: number;
  /** The words for the buttons that start a garden: the founding claim for a founding visitor, null for the page's own. */
  startKey: string | null;
  /** How many founding gardeners the founding welcome names. */
  foundingPlaces: number;
  /** The owner's own garden address on Grow, without its "@". */
  ownerSlug: string;
  /** The plan comparison's rows, which the price cards draw their lines from. */
  rows: PlanRow[];
  /** Whether every signed-out visitor sees the remodelled page: the Grow setting landing.V2, sent here so the page has no default of its own. */
  v2: boolean;
}

/**
 * What the landing page says about price, the trial, Free and the founding
 * welcome. Needs no sign-in.
 *
 * @param visit.foundingCode - The founding code this visit's link brought, if any.
 * @param visit.from - 'ig' or 'fb' for a visitor from the owner's own links, if any.
 * @returns The facts.
 */
export async function getLandingFacts(visit: { foundingCode?: string | null; from?: string | null }): Promise<LandingFacts> {
  // The page's language, so the founder price comes back said in it.
  const params: Record<string, string> = { lang: getLanguage() };
  if (visit.foundingCode) params.founding = visit.foundingCode;
  if (visit.from) params.from = visit.from;
  const response = await apiClient.get<LandingFacts & { success: boolean }>('/billing/landing', { params });
  return response.data;
}
